import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Icon, Button, ButtonTooltip } from '@evertrue/et-components';
import EverTrue from 'app';
import Api from 'entities/helpers/api';
import WaitCountdown from './wait-countdown';
import AddTask from 'apps/tasks/components/create/add-task';
import cadenceGqlCall from '../../utils/cadence-gql-call';
import { CADENCE_MEMBER_ACTIVITY_QUERY, MEMBER_ITERATIONS_QUERY } from '../../utils/cadence-queries';
import { describeRecipientSuccess } from '../../utils/cadence-data-mappers';
import { STATUS_COLORS } from '../../utils/member-status';
import { getStepIcon, getStepBadgeColor, getStepModeSubline } from '../../utils/cadence-step-utils';
import StepLabelBlock from '../shared/step-label-block';
import { isEarlyExit, isActiveMember, getDisplayStatusLabel } from '../../utils/cadence-enums';
import { CadenceFlowVisualization } from '../contact-cadences-tab';
import { fetchUsersById } from 'apps/volunteers/vms-queries';

/** Activity types to display as sub-events inside step cards. */
const VISIBLE_ACTIVITY_TYPES = new Set([
  'enrolled',
  'removed',
  'completed',
  'email_sent',
  'email_delivered',
  'email_opened',
  'email_clicked',
  'email_replied',
  'email_bounced',
  'email_send_failed',
  'unsubscribed',
  'manual_task_created',
  'manual_task_completed',
  'task_created',
  'task_completed',
  'task_skipped',
  'step_skipped',
  'waiting',
  'condition_evaluated',
]);

/** Display info for visible activity types. */
const ACTIVITY_DISPLAY = {
  enrolled: { label: 'Enrolled', icon: 'person-add', color: '#2563eb' },
  removed: { label: 'Removed', icon: 'person-remove', color: '#dc2626' },
  completed: { label: 'Completed', icon: 'check-circle', color: '#16a34a' },
  email_sent: { label: 'Sent', icon: 'send', color: '#2563eb' },
  email_delivered: { label: 'Delivered', icon: 'check-circle', color: '#16a34a' },
  email_opened: { label: 'Opened', icon: 'eye', color: '#7c3aed' },
  email_clicked: { label: 'Link Clicked', icon: 'eye', color: '#7c3aed' },
  email_replied: { label: 'Replied', icon: 'send', color: '#16a34a' },
  email_bounced: { label: 'Bounced', icon: 'close', color: '#dc2626' },
  email_send_failed: { label: 'Send Failed', icon: 'close', color: '#dc2626' },
  unsubscribed: { label: 'Unsubscribed', icon: 'close', color: '#d97706' },
  manual_task_created: { label: 'Task Created', icon: 'access-time', color: '#d97706' },
  manual_task_completed: { label: 'Task Completed', icon: 'check-circle', color: '#16a34a' },
  task_created: { label: 'Task Created', icon: 'access-time', color: '#d97706' },
  task_completed: { label: 'Task Completed', icon: 'check-circle', color: '#16a34a' },
  task_skipped: { label: 'Task Skipped', icon: 'close', color: '#d97706' },
  step_skipped: { label: 'Skipped', icon: 'close', color: '#d97706' },
  waiting: { label: 'Waiting', icon: 'access-time', color: '#6b7280' },
  condition_evaluated: { label: 'Condition Checked', icon: 'check-circle', color: '#7c3aed' },
};

/**
 * Derive step status from matched activities, in priority order.
 *
 * Spec statuses:
 *   n_a             — no send attempted, no skip reason (pending / unreached)
 *   skipped_manual  — user manually skipped this step
 *   skipped_auto    — send skipped automatically (missing email, merge field, etc.)
 *   failed_send     — send attempted but bounced/blocked
 *   delivered       — email reached inbox
 *
 * Additional display-only statuses for richer UI:
 *   opened          — email was opened
 *   sent            — email dispatched, delivery not yet confirmed
 *   in_progress     — step is currently executing
 *   task_pending    — manual task created, waiting for completion
 *   task_completed  — manual task completed
 */
const deriveStepStatus = (activities, isCurrentStep, isMemberNotActive = false) => {
  const types = new Set(activities.map((a) => a.activityType));

  // --- Definitive outcomes (show regardless of member status) ---

  // Failed send
  if (types.has('email_bounced') || types.has('email_send_failed')) return 'failed_send';

  // Skips — distinguish manual from automatic
  if (types.has('step_skipped') || types.has('task_skipped')) {
    const skipAct = activities.find((a) => a.activityType === 'step_skipped' || a.activityType === 'task_skipped');
    const reason = skipAct?.details?.skip_reason || skipAct?.details?.reason || '';
    return reason === 'user_skip' ? 'skipped_manual' : 'skipped_auto';
  }

  // Positive email engagement (ordered by depth)
  if (types.has('email_opened') || types.has('email_clicked')) return 'delivered';
  if (types.has('email_delivered')) return 'delivered';
  if (types.has('email_sent')) return 'sent';

  // Task completed is definitive
  if (types.has('task_completed') || types.has('manual_task_completed')) return 'task_completed';

  // Member-level completion activity
  if (types.has('completed')) return 'completed';

  // --- Member is not active (paused, removed, completed, archived, early_exit) ---
  // Nothing further will happen — pending tasks won't complete, unreached steps won't run.
  // This MUST be checked before any in-progress/pending states.
  if (isMemberNotActive) return 'not_executed';

  // --- In-progress states (only when member is actively progressing) ---
  if (types.has('task_created') || types.has('manual_task_created')) return 'task_pending';
  if (isCurrentStep) return 'in_progress';
  return 'n_a';
};

/** Border color for step card based on status. */
const STEP_STATUS_CLASS = {
  failed_send: 'is-failed',
  skipped_manual: 'is-greyed',
  skipped_auto: 'is-greyed',
  delivered: 'is-delivered',
  sent: 'is-active',
  task_completed: 'is-delivered',
  task_pending: 'is-active',
  completed: 'is-delivered',
  in_progress: 'is-active',
  not_executed: 'is-greyed',
  n_a: 'is-greyed',
};

/** Label + icon for step status badge. */
const STEP_STATUS_DISPLAY = {
  failed_send: { label: 'Failed Send', icon: 'close', color: '#dc2626' },
  skipped_manual: { label: 'Task Skipped', icon: 'close', color: '#d97706' },
  skipped_auto: { label: 'Skipped', icon: 'close', color: '#d97706' },
  delivered: { label: 'Delivered', icon: 'check-circle', color: '#16a34a' },
  sent: { label: 'Sent', icon: 'send', color: '#2563eb' },
  task_completed: { label: 'Task Completed', icon: 'check-circle', color: '#16a34a' },
  task_pending: { label: 'Task Pending', icon: 'access-time', color: '#d97706' },
  completed: { label: 'Completed', icon: 'check-circle', color: '#16a34a' },
  in_progress: { label: 'In Progress', icon: 'access-time', color: '#2563eb' },
  not_executed: { label: 'Not executed', icon: 'close', color: '#9ca3af' },
  n_a: { label: 'N/A', icon: 'access-time', color: '#6b7280' },
};

/** Strip HTML tags and return first N characters of text. */
const getPreviewText = (eventConfig, maxLen = 100) => {
  if (!eventConfig) return '';
  if (eventConfig.text_body) {
    return eventConfig.text_body.slice(0, maxLen).trim();
  }
  if (eventConfig.html_body) {
    const stripped = eventConfig.html_body
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return stripped.slice(0, maxLen).trim();
  }
  return '';
};

const getStepTypeIcon = getStepIcon;

/**
 * Build the step timeline from step definitions + activities.
 * Returns ordered array of { type: 'enrolled'|'wait'|'step'|'exit', ...data }
 */
const buildStepTimeline = (steps, activities, recipient) => {
  const timeline = [];

  // Enrolled entry
  timeline.push({ type: 'enrolled', enrolledAt: recipient.enrolledAt });

  // Index activities by cadenceEventId (only visible types)
  const actsByEvent = {};
  for (const act of activities) {
    if (!VISIBLE_ACTIVITY_TYPES.has(act.activityType)) continue;
    const eid = act.cadenceEventId ? String(act.cadenceEventId) : null;
    if (!eid) continue;
    if (!actsByEvent[eid]) actsByEvent[eid] = [];
    actsByEvent[eid].push(act);
  }

  // Walk steps in order
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];

    // Insert preceding wait
    if (step.precedingWait) {
      const { days, hours, minutes } = step.precedingWait;
      if (days || hours || minutes) {
        timeline.push({ type: 'wait', days, hours, minutes });
      }
    }

    const matched = actsByEvent[String(step.id)] || [];
    // Sort matched activities chronologically
    matched.sort((a, b) => new Date(a.activityAt) - new Date(b.activityAt));

    const isCurrentStep = String(step.id) === String(recipient.currentEventId);
    const memberNotActive = !isActiveMember(recipient.displayStatus);
    const status = deriveStepStatus(matched, isCurrentStep, memberNotActive);

    timeline.push({
      type: 'step',
      stepIndex: i,
      step,
      activities: matched,
      status,
      isCurrentStep,
    });
  }

  // Exit entry if removed or completed
  const isMemberDone = recipient.displayStatus === 'completed' || isEarlyExit(recipient.displayStatus);
  if (isMemberDone) {
    timeline.push({
      type: 'exit',
      status: recipient.status,
      isSuccessful: recipient.isSuccessful,
      recipient,
    });

    // Mark steps after exit as unreachable
    let foundExit = false;
    for (const entry of timeline) {
      if (entry.type === 'exit') {
        foundExit = true;
        continue;
      }
      if (foundExit && entry.type === 'step') {
        entry.unreachable = true;
      }
    }

    // Also mark steps that were never reached (no activities AND after the last activity)
    const lastActivityEventId =
      activities.length > 0 ? String(activities[activities.length - 1]?.cadenceEventId) : null;
    let passedLastActivity = false;
    for (const entry of timeline) {
      if (entry.type === 'step') {
        if (passedLastActivity && entry.activities.length === 0) {
          entry.unreachable = true;
        }
        if (lastActivityEventId && String(entry.step?.id) === lastActivityEventId) {
          passedLastActivity = true;
        }
      }
    }
  }

  return timeline;
};

/**
 * Step card showing email name, delivery sub-events, and preview text.
 */
const StepCard = ({ stepIndex, step, activities, status, isCurrentStep, formatDate, onTaskClick, unreachable }) => {
  const dimmed = unreachable || status === 'not_executed';
  const statusClass = dimmed ? 'is-greyed' : STEP_STATUS_CLASS[status] || 'is-greyed';
  const statusDisplay = dimmed
    ? { label: '', icon: '', color: '#9ca3af' }
    : STEP_STATUS_DISPLAY[status] || STEP_STATUS_DISPLAY.pending;
  const previewText = getPreviewText(step.eventConfig);
  const stepTitle = step.name && step.name !== step.label ? step.name : undefined;

  return (
    <div className={`recipient-detail-panel--step ${statusClass}`} style={dimmed ? { opacity: 0.5 } : undefined}>
      <div className="recipient-detail-panel--step-header">
        <div className="recipient-detail-panel--step-title">
          <Icon
            icon={dimmed ? 'radio-button-unchecked' : getStepTypeIcon(step.type, step.eventConfig?.action_type)}
            style={{ fontSize: 16, color: dimmed ? '#9ca3af' : statusDisplay.color, flexShrink: 0 }}
          />
          <div>
            <StepLabelBlock
              title={stepTitle}
              typeLabel={step.label}
              typeIcon={getStepTypeIcon(step.type, step.eventConfig?.action_type)}
              badgeColor={getStepBadgeColor(step)}
              modeSubline={getStepModeSubline(step)}
              dimmed={dimmed}
            />
            {dimmed && <div className="recipient-detail-panel--not-reached-label">Not reached - cadence ended</div>}
          </div>
        </div>
        {!dimmed && (
          <div className="recipient-detail-panel--step-status-indicator">
            <Icon icon={statusDisplay.icon} style={{ fontSize: 14, color: statusDisplay.color }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: statusDisplay.color }}>{statusDisplay.label}</span>
          </div>
        )}
      </div>

      <div className="recipient-detail-panel--step-body">
        {/* Delivery sub-events */}
        {!dimmed && activities.length > 0 && (
          <div className="recipient-detail-panel--step-subevents">
            {activities.map((act) => {
              const info = ACTIVITY_DISPLAY[act.activityType];
              if (!info) return null;
              const details = act.details || {};
              const taskId = details.journeys_task_id;
              const isTaskLink = act.activityType === 'task_created' && taskId;
              const label = isTaskLink ? details.title || info.label : info.label;

              return (
                <div
                  key={act.id}
                  className={`recipient-detail-panel--step-subevent${isTaskLink ? ' is-clickable' : ''}`}
                  onClick={isTaskLink && onTaskClick ? () => onTaskClick(taskId) : undefined}
                  role={isTaskLink ? 'link' : undefined}
                  style={isTaskLink ? { cursor: 'pointer' } : undefined}
                >
                  <Icon icon={info.icon} style={{ fontSize: 14, color: info.color }} />
                  <span className="recipient-detail-panel--step-subevent-label">{label}</span>
                  <span className="recipient-detail-panel--step-subevent-date">{formatDate(act.activityAt)}</span>
                  {isTaskLink && <Icon icon="open-in-new" className="recipient-detail-panel--task-link-icon" />}
                </div>
              );
            })}
          </div>
        )}

        {/* Email preview text */}
        {!dimmed && previewText && status !== 'pending' && (
          <div className="recipient-detail-panel--step-preview">{previewText}...</div>
        )}

        {/* Pending state */}
        {!dimmed && status === 'pending' && !isCurrentStep && (
          <div className="recipient-detail-panel--step-na">Pending</div>
        )}
      </div>
    </div>
  );
};

StepCard.propTypes = {
  stepIndex: PropTypes.number.isRequired,
  step: PropTypes.object.isRequired,
  activities: PropTypes.array.isRequired,
  status: PropTypes.string.isRequired,
  isCurrentStep: PropTypes.bool,
  formatDate: PropTypes.func.isRequired,
  onTaskClick: PropTypes.func,
  unreachable: PropTypes.bool,
};

/**
 * Compact wait bar between step cards.
 */
const WaitCard = ({ days, hours, minutes, isActive, targetDate }) => {
  let label = 'Wait';
  if (days) label = `Wait ${days} day${days !== 1 ? 's' : ''}`;
  else if (hours) label = `Wait ${hours} hour${hours !== 1 ? 's' : ''}`;
  else if (minutes) label = `Wait ${minutes} minute${minutes !== 1 ? 's' : ''}`;

  return (
    <div className={`recipient-detail-panel--wait-interstitial${isActive ? ' is-active' : ''}`}>
      <Icon icon="access-time" style={{ fontSize: 16, color: isActive ? '#2563eb' : '#6b7280' }} />
      <span className="recipient-detail-panel--wait-label">{label}</span>
      {isActive && targetDate && <WaitCountdown targetDate={targetDate} />}
    </div>
  );
};

WaitCard.propTypes = {
  days: PropTypes.number,
  hours: PropTypes.number,
  minutes: PropTypes.number,
  isActive: PropTypes.bool,
  targetDate: PropTypes.string,
};

/**
 * Enrolled entry at the start of the timeline.
 */
const EnrolledEntry = ({ date, formatDate }) => (
  <div className="recipient-detail-panel--enrolled-entry">
    <Icon icon="group-add" className="recipient-detail-panel--enrolled-icon" />
    <span>Enrolled</span>
    <span className="recipient-detail-panel--entry-date">{formatDate(date)}</span>
  </div>
);

EnrolledEntry.propTypes = {
  date: PropTypes.string,
  formatDate: PropTypes.func.isRequired,
};

/** Resolve "User #ID" tokens in text to real user names via async fetch. */
const useResolvedUserText = (rawText) => {
  const [resolved, setResolved] = useState(rawText || '');
  useEffect(() => {
    if (!rawText) {
      setResolved('');
      return;
    }
    const matches = [...rawText.matchAll(/User #(\d+)/g)];
    if (!matches.length) {
      setResolved(rawText);
      return;
    }
    const ids = [...new Set(matches.map((m) => Number(m[1])))];
    fetchUsersById(ids)
      .then((users) => {
        let text = rawText;
        (users || []).forEach((u) => {
          if (u?.name && u.name !== 'Removed User') {
            text = text.replace(new RegExp(`User #${u.id}`, 'g'), u.name);
          }
        });
        setResolved(text);
      })
      .catch(() => setResolved(rawText));
  }, [rawText]);
  return resolved;
};

/**
 * Exit entry at the end of the timeline (completed or removed).
 * Shows specific exit type and reason per PM spec.
 */
const ExitEntry = ({ status, isSuccessful, recipient }) => {
  const isCompleted = status === 'completed';
  const displayStatus = recipient?.displayStatus || (isCompleted ? 'completed' : 'early_exit_manual');
  const statusStyle = STATUS_COLORS[displayStatus] || STATUS_COLORS.removed;
  const explanation = useResolvedUserText(recipient?.statusExplanation || null);

  const exitLabel = getDisplayStatusLabel(recipient) || (isCompleted ? 'Completed' : 'Removed');

  return (
    <div className={`recipient-detail-panel--exit-entry${isCompleted ? ' is-success' : ' is-removed'}`}>
      <div className="recipient-detail-panel--exit-entry-header">
        <Icon
          icon={isSuccessful ? 'star' : isCompleted ? 'flag' : 'close'}
          style={{ fontSize: 16, color: isCompleted ? '#16a34a' : '#dc2626' }}
        />
        <span
          className="recipient-detail-panel--exit-badge"
          style={{ background: statusStyle.bg, color: statusStyle.color }}
        >
          {exitLabel}
        </span>
        {isSuccessful && <span className="recipient-detail-panel--exit-success-chip">Success Reached</span>}
      </div>
      {explanation && (
        <div className="recipient-detail-panel--exit-reason">
          <Icon icon="info-outline" className="recipient-detail-panel--exit-reason-icon" />
          <span>{explanation}</span>
        </div>
      )}
      {recipient?.removedAt && !isCompleted && (
        <div className="recipient-detail-panel--exit-date">
          Removed on{' '}
          {new Date(
            recipient.removedAt.endsWith('Z') ? recipient.removedAt : recipient.removedAt + 'Z'
          ).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      )}
    </div>
  );
};

ExitEntry.propTypes = {
  status: PropTypes.string.isRequired,
  isSuccessful: PropTypes.bool,
  recipient: PropTypes.object,
};

/**
 * Slide-in panel showing recipient details from real cadence member data.
 * Builds a definition-driven step timeline from step definitions + activities.
 */
const RecipientDetailPanel = ({
  recipient,
  cadenceId,
  cadenceName,
  cadence: rawCadence,
  steps = [],
  onClose,
  onRemove,
  onSkipStep,
  successMetric,
}) => {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskModalObj, setTaskModalObj] = useState(null);

  const handleTaskClick = async (taskId) => {
    try {
      const task = await Api.JOURNEYS.TASK.get({ urlArgs: { id: taskId } });
      if (task) {
        // Enrich with cadence name so the badge in the task modal shows it
        task._cadenceName = cadenceName;
        setTaskModalObj(task);
        setTaskModalOpen(true);
      }
    } catch {
      EverTrue.Alert.error('Could not load task details');
    }
  };
  const [statusExpanded, setStatusExpanded] = useState(false);
  const [successExpanded, setSuccessExpanded] = useState(false);
  const [flowOpen, setFlowOpen] = useState(false);
  const [iterations, setIterations] = useState([]);

  useEffect(() => {
    if (!recipient?.id) return;
    cadenceGqlCall(MEMBER_ITERATIONS_QUERY, { cadenceMemberId: String(recipient.id) })
      .then((data) => setIterations(data?.memberIterations || []))
      .catch(() => setIterations([]));
  }, [recipient?.id]);

  useEffect(() => {
    if (!recipient?.id) {
      setActivitiesLoading(false);
      return;
    }
    let cancelled = false;
    setActivitiesLoading(true);

    cadenceGqlCall(CADENCE_MEMBER_ACTIVITY_QUERY, { cadenceMemberId: String(recipient.id) })
      .then((data) => {
        if (!cancelled) {
          const conn = data.cadenceMemberActivity || {};
          setActivities((conn.edges || []).map((e) => e.node));
        }
      })
      .catch(() => {
        if (!cancelled) setActivities([]);
      })
      .finally(() => {
        if (!cancelled) setActivitiesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [recipient?.id]);

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await onRemove(recipient);
    } finally {
      setRemoving(false);
      setConfirmRemove(false);
    }
  };

  const handleSkipStep = async () => {
    if (!onSkipStep || !cadenceId || !recipient?.id) return;
    setSkipping(true);
    try {
      await onSkipStep(cadenceId, recipient.id);
    } finally {
      setSkipping(false);
    }
  };

  const statusStyle = STATUS_COLORS[recipient.displayStatus] || STATUS_COLORS.active;
  const statusExplanation = useResolvedUserText(recipient.statusExplanation || null);

  // Find current step by matching currentEventId to the cadence steps
  const currentStepIndex = steps.findIndex((s) => String(s.id) === String(recipient.currentEventId));

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Build step timeline
  const timeline = useMemo(() => buildStepTimeline(steps, activities, recipient), [steps, activities, recipient]);

  // Determine if recipient is currently blocked (waiting on timer or manual task)
  const isActive = recipient.displayStatus === 'in_progress' || recipient.displayStatus === 'enrolled';
  const currentStep = currentStepIndex >= 0 ? steps[currentStepIndex] : null;
  const isOnWaitTimer = isActive && !!recipient.nextEventScheduledAt;
  const isOnManualTask =
    isActive &&
    currentStep &&
    (currentStep.type === 'task' || (currentStep.type === 'email' && currentStep.isAutomated === false));
  const isWaiting = isOnWaitTimer;
  const canSkipStep = isOnWaitTimer || isOnManualTask;

  // Find which wait entry is "active" — it's the one just before the current step
  const activeWaitIndex = useMemo(() => {
    if (!isWaiting || currentStepIndex < 0) return -1;
    // Find the step entry for the current step, then the wait just before it
    for (let i = timeline.length - 1; i >= 0; i--) {
      if (timeline[i].type === 'step' && timeline[i].stepIndex === currentStepIndex) {
        // Walk back to find preceding wait
        for (let j = i - 1; j >= 0; j--) {
          if (timeline[j].type === 'wait') return j;
          if (timeline[j].type !== 'wait') break;
        }
      }
    }
    return -1;
  }, [timeline, isWaiting, currentStepIndex]);

  return (
    <div className="recipient-detail-overlay" onClick={onClose}>
      <div
        className="recipient-detail-panel"
        role="dialog"
        aria-label="Recipient Activity"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Panel Header — avatar + name + contact ID + close */}
        <div className="recipient-detail-panel--header">
          <div className="recipient-detail-panel--identity">
            {recipient.contactImage ? (
              <img src={recipient.contactImage} alt="" className="recipient-detail-panel--avatar" />
            ) : (
              <div className="recipient-detail-panel--avatar-placeholder">
                <Icon icon="person" />
              </div>
            )}
            <div>
              <h2 className="recipient-detail-panel--name">
                <button
                  type="button"
                  className="recipient-detail-panel--name-link"
                  onClick={() => EverTrue.Navigator(`/contact/${recipient.contactId}`, true)}
                >
                  {recipient.contactName || `Contact ${recipient.contactId}`}
                </button>
              </h2>
            </div>
          </div>
          <button type="button" className="recipient-detail-panel--close" onClick={onClose}>
            <Icon icon="close" />
          </button>
        </div>

        {/* Meta rows — Cadence, Enrolled, Status (expandable), Cadence Success (expandable) */}
        <div className="recipient-detail-panel--meta">
          <div className="recipient-detail-panel--meta-row">
            <span className="recipient-detail-panel--meta-label">Cadence</span>
            <span className="recipient-detail-panel--meta-link">{cadenceName}</span>
          </div>
          <div className="recipient-detail-panel--meta-row">
            <span className="recipient-detail-panel--meta-label">Enrolled</span>
            <span>{formatDate(recipient.enrolledAt)}</span>
          </div>

          {iterations.length > 1 && (
            <div className="recipient-detail-panel--meta-row">
              <span className="recipient-detail-panel--meta-label">Iteration</span>
              <select
                className="recipient-detail-panel--iteration-select"
                defaultValue={iterations[iterations.length - 1]?.id}
              >
                {[...iterations].reverse().map((it) => (
                  <option key={it.id} value={it.id}>
                    #{it.iterationNumber} — {formatDate(it.enrolledAt)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Status row — expandable badge */}
          <div className="recipient-detail-panel--meta-row">
            <span className="recipient-detail-panel--meta-label">Status</span>
            <button
              type="button"
              className="recipient-detail-panel--expandable-badge"
              style={{ background: statusStyle.bg, color: statusStyle.color }}
              onClick={() => setStatusExpanded((v) => !v)}
            >
              {getDisplayStatusLabel(recipient)}
              {statusExplanation && (
                <Icon
                  icon={statusExpanded ? 'chevron-up' : 'chevron-down'}
                  className="recipient-detail-panel--badge-chevron"
                />
              )}
            </button>
          </div>
          {statusExpanded && statusExplanation && (
            <div className="recipient-detail-panel--expand-card" style={{ borderLeftColor: statusStyle.color }}>
              <strong style={{ color: statusStyle.color }}>{getDisplayStatusLabel(recipient)}</strong>
              <p>{statusExplanation}</p>
            </div>
          )}

          {/* Cadence Success row — always visible */}
          <>
            <div className="recipient-detail-panel--meta-row">
              <span className="recipient-detail-panel--meta-label">Cadence Success</span>
              <button
                type="button"
                className="recipient-detail-panel--expandable-badge"
                style={
                  recipient.isSuccessful
                    ? { background: '#dcfce7', color: '#16a34a' }
                    : { background: '#fef3c7', color: '#92400E' }
                }
                onClick={() => setSuccessExpanded((v) => !v)}
              >
                {recipient.isSuccessful ? 'Success Reached' : 'Success Not Reached'}
                <Icon
                  icon={recipient.isSuccessful ? 'check' : 'access-time'}
                  className="recipient-detail-panel--badge-chevron"
                />
                {successMetric && (
                  <Icon
                    icon={successExpanded ? 'chevron-up' : 'chevron-down'}
                    className="recipient-detail-panel--badge-chevron"
                  />
                )}
              </button>
            </div>
            {successExpanded && (
              <div
                className="recipient-detail-panel--expand-card"
                style={{
                  borderLeftColor: recipient.isSuccessful ? '#16a34a' : '#dc2626',
                }}
              >
                <span className="recipient-detail-panel--expand-card-label">SUCCESS METRIC</span>
                {successMetric ? (
                  <>
                    <strong>{successMetric.label || successMetric.metricType}</strong>
                    {successMetric.targetPercentage != null && (
                      <p className="recipient-detail-panel--expand-card-target">
                        Target: {successMetric.targetPercentage}% of recipients
                      </p>
                    )}
                    <p className="recipient-detail-panel--expand-card-description">
                      {describeRecipientSuccess(successMetric.metricType, successMetric.metricConfig || {})}
                    </p>
                  </>
                ) : (
                  <strong>Success metric</strong>
                )}
                {recipient.isSuccessful ? (
                  <p className="recipient-detail-panel--expand-card-success">
                    <Icon icon="check" className="recipient-detail-panel--badge-chevron" /> Success Reached
                    {recipient.successAchievedAt && ` on ${formatDate(recipient.successAchievedAt)}`}
                  </p>
                ) : (
                  <p className="recipient-detail-panel--expand-card-fail">Success has not been reached yet.</p>
                )}
              </div>
            )}
          </>

          {/* Skip Step action for active members blocked on wait or manual task */}
          {onSkipStep && canSkipStep && (
            <div className="recipient-detail-panel--skip-action">
              <Button type="secondary" onClick={handleSkipStep} disabled={skipping}>
                <Icon icon="double-chevron-right" />
                <span>{skipping ? 'Skipping...' : isOnManualTask ? 'Skip Task' : 'Skip Wait'}</span>
              </Button>
            </div>
          )}
        </div>

        {/* Email Activity Summary */}
        <div className="recipient-detail-panel--email-summary">
          <h3 className="recipient-detail-panel--section-title">Email Activity</h3>
          <div className="recipient-detail-panel--email-stats">
            <div className="recipient-detail-panel--email-stat">
              <Icon
                icon="send"
                className="recipient-detail-panel--email-stat-icon recipient-detail-panel--email-stat-icon--sent"
              />
              <span className="recipient-detail-panel--email-stat-value">{recipient.totalEmailsSent ?? 0}</span>
              <span className="recipient-detail-panel--email-stat-label">Sent</span>
            </div>
            <div className="recipient-detail-panel--email-stat">
              <Icon
                icon="eye"
                className="recipient-detail-panel--email-stat-icon recipient-detail-panel--email-stat-icon--opened"
              />
              <span className="recipient-detail-panel--email-stat-value">{recipient.totalEmailsOpened ?? 0}</span>
              <span className="recipient-detail-panel--email-stat-label">Opened</span>
            </div>
            <div className="recipient-detail-panel--email-stat">
              <Icon
                icon="reply"
                className="recipient-detail-panel--email-stat-icon recipient-detail-panel--email-stat-icon--replied"
              />
              <span className="recipient-detail-panel--email-stat-value">{recipient.totalEmailsReplied ?? 0}</span>
              <span className="recipient-detail-panel--email-stat-label">Replied</span>
            </div>
          </div>
        </div>

        {/* Cadence Flow — collapsible, closed by default (matches modal behavior) */}
        <div className="recipient-detail-panel--progress">
          <button
            type="button"
            className="cadence-profile-modal--collapse-toggle"
            onClick={() => setFlowOpen((v) => !v)}
          >
            <Icon icon="list" className="cadence-profile-modal--collapse-icon" />
            <span className="cadence-profile-modal--collapse-label">CADENCE FLOW</span>
            <Icon icon={flowOpen ? 'chevron-up' : 'chevron-down'} className="cadence-profile-modal--collapse-chevron" />
          </button>

          {flowOpen && activitiesLoading && (
            <div className="recipient-detail-panel--activity-loading">Loading activity...</div>
          )}

          {flowOpen && !activitiesLoading && rawCadence && (
            <CadenceFlowVisualization
              cadence={rawCadence}
              activities={activities}
              currentEventId={recipient.currentEventId}
              memberStatus={recipient.status}
              displayStatus={recipient.displayStatus}
              compact
            />
          )}

          {flowOpen && !activitiesLoading && !rawCadence && (
            <div className="recipient-detail-panel--grouped-timeline">
              {timeline.map((entry, idx) => {
                if (entry.type === 'enrolled') {
                  return <EnrolledEntry key="enrolled" date={entry.enrolledAt} formatDate={formatDate} />;
                }
                if (entry.type === 'wait') {
                  const isActiveWait = idx === activeWaitIndex;
                  return (
                    <WaitCard
                      key={`wait-${idx}`}
                      days={entry.days}
                      hours={entry.hours}
                      minutes={entry.minutes}
                      isActive={isActiveWait}
                      targetDate={isActiveWait ? recipient.nextEventScheduledAt : null}
                    />
                  );
                }
                if (entry.type === 'step') {
                  return (
                    <StepCard
                      key={`step-${entry.step.id}`}
                      stepIndex={entry.stepIndex}
                      step={entry.step}
                      activities={entry.activities}
                      status={entry.status}
                      isCurrentStep={entry.isCurrentStep}
                      formatDate={formatDate}
                      onTaskClick={handleTaskClick}
                      unreachable={entry.unreachable}
                    />
                  );
                }
                if (entry.type === 'exit') {
                  return (
                    <ExitEntry
                      key="exit"
                      status={entry.status}
                      isSuccessful={entry.isSuccessful}
                      recipient={entry.recipient}
                    />
                  );
                }
                return null;
              })}
            </div>
          )}
        </div>

        {/* Remove Action */}
        {onRemove && !isEarlyExit(recipient.displayStatus) && (
          <div className="recipient-detail-panel--actions">
            {recipient.displayStatus === 'completed' ? (
              <ButtonTooltip
                contentWidth={280}
                trigger={
                  <span>
                    <Button type="destroy" disabled>
                      <Icon icon="close" /> Remove from Cadence
                    </Button>
                  </span>
                }
              >
                This recipient has already completed the cadence and cannot be removed.
              </ButtonTooltip>
            ) : confirmRemove ? (
              <div className="recipient-detail-panel--confirm">
                <span>Remove from this cadence?</span>
                <div className="recipient-detail-panel--confirm-buttons">
                  <Button type="destroy" onClick={handleRemove} disabled={removing}>
                    {removing ? 'Removing...' : 'Yes, Remove'}
                  </Button>
                  <Button type="secondary" onClick={() => setConfirmRemove(false)} disabled={removing}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button type="destroy" onClick={() => setConfirmRemove(true)}>
                <Icon icon="close" /> Remove from Cadence
              </Button>
            )}
          </div>
        )}
      </div>

      <AddTask
        isOpen={taskModalOpen}
        closeModal={() => setTaskModalOpen(false)}
        handleClose={() => setTaskModalOpen(false)}
        taskObj={taskModalObj}
        contact={{ id: recipient?.contactId, name: recipient?.contactName || '', avatar: '' }}
      />
    </div>
  );
};

RecipientDetailPanel.propTypes = {
  recipient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    contactId: PropTypes.number,
    contactName: PropTypes.string,
    contactEmail: PropTypes.string,
    contactPhone: PropTypes.string,
    contactImage: PropTypes.string,
    status: PropTypes.string,
    displayStatus: PropTypes.string,
    statusExplanation: PropTypes.string,
    enrolledAt: PropTypes.string,
    totalEmailsSent: PropTypes.number,
    totalEmailsOpened: PropTypes.number,
    totalEmailsReplied: PropTypes.number,
    isSuccessful: PropTypes.bool,
    successAchievedAt: PropTypes.string,
    removedAt: PropTypes.string,
    removedByUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    removedByUserName: PropTypes.string,
    currentEventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nextEventScheduledAt: PropTypes.string,
    issues: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        message: PropTypes.string,
        code: PropTypes.string,
        step: PropTypes.string,
      })
    ),
  }).isRequired,
  cadenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cadenceName: PropTypes.string.isRequired,
  cadence: PropTypes.object,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  onClose: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  onSkipStep: PropTypes.func,
  successMetric: PropTypes.shape({
    label: PropTypes.string,
    metricType: PropTypes.string,
    metricConfig: PropTypes.object,
    targetPercentage: PropTypes.number,
    description: PropTypes.string,
  }),
};

export default RecipientDetailPanel;
